import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useRecentlyViewedProductsQuery } from '@src/queries/RecentlyViewedProductsQuery.generated'
import { AnalyticsDispType } from '@src/types/graphql-types'
import { makeStyles } from 'tss-react/mui'
import { sendEmptyRecommendationGA4Event } from '@utils/analytics'
import { ErpType } from '@utils/useProductErpType'
import { useCurrentUser } from '@utils/useCurrentUser'
import NewPdpDynamicProductCarousel from './NewPdpDynamicProductCarousel'
import { CarouselProductTypes } from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'

export const useStyles = makeStyles()((theme) => {
  return {
    recentlyViewedContainer: {
      [theme.breakpoints.down('md')]: {
        paddingBottom: theme.spacing(2),
      },
    },
  }
})

interface NewRecentlyViewedProductsCarouselProps {
  dispType: AnalyticsDispType
  className?: string
  productKey?: string
  brand?: string
  slidesToShow?: number
  setShouldDisplay?: Dispatch<SetStateAction<boolean>>
  condensed?: boolean
  productNumber?: string
}

const NewRecentlyViewedProductsCarousel: React.FC<
  NewRecentlyViewedProductsCarouselProps
> = ({
  dispType,
  className,
  productKey = '',
  brand = '',
  slidesToShow,
  setShouldDisplay,
  condensed = false,
  productNumber,
}) => {
  const { classes, cx } = useStyles()
  const { isBlueB2BUser } = useCurrentUser()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const { data, loading } = useRecentlyViewedProductsQuery({
    variables: {
      dispType,
      productKey,
      brand,
      ...(isBlueB2BUser ? { erpType: ErpType.Blue } : {}),
    },
    ssr: false,
    fetchPolicy: 'no-cache',
    skip: !isMounted,
    onCompleted: (loadedData) => {
      if (
        !loadedData ||
        loadedData?.getRecentlyViewedProducts?.products.length === 0
      ) {
        sendEmptyRecommendationGA4Event({
          action: 'pdp load',
          detail: productKey.toLowerCase(),
          section: 'recently viewed products',
          productId: productNumber?.toLowerCase(),
          productBrand: brand.toLowerCase(),
        })
      }
    },
  })

  const recentlyViewProducts = data?.getRecentlyViewedProducts?.products || []
  const limitedProducts = recentlyViewProducts.slice(0, 10)
  const shouldRender = limitedProducts.length > 0

  useEffect(() => {
    if (setShouldDisplay) {
      setShouldDisplay(shouldRender)
    }
  }, [shouldRender, loading])

  if (loading) return null

  return shouldRender ? (
    <div className={cx(className, classes.recentlyViewedContainer)}>
      <NewPdpDynamicProductCarousel
        type={CarouselProductTypes.RecentlyViewed}
        products={limitedProducts}
        slidesToShow={slidesToShow}
        condensed={condensed}
      />
    </div>
  ) : null
}

export default NewRecentlyViewedProductsCarousel
